import { useLocation } from 'react-router-dom';
import { useRef, useState, useEffect } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useConnectivityStore } from 'src/features/connectivity/connectivity-store';
import { connectivityCoordinator } from 'src/features/connectivity/connectivity-service';

import { SplashScreen } from 'src/components/loading-screen';

import useAuthStore, { waitForAuthReady } from '../store/firebase/auth-store';

// ----------------------------------------------------------------------

const getLoginPath = (type: string) => {
  switch (type) {
    case 'dashboard':
      return paths.auth.login;
    case 'companion':
      return paths.auth.loginCompanion;
    default:
      return paths.auth.login;
  }
};

// ----------------------------------------------------------------------

type Props = {
  type: 'dashboard' | 'companion';
  children: React.ReactNode;
};

export default function AuthGuard({ children, type = 'dashboard' }: Props) {
  const router = useRouter();
  const location = useLocation();
  const authenticated = useAuthStore((state) => state.authenticated);
  const authInitialized = useAuthStore((state) => state.authInitialized);
  const authRestorationStatus = useAuthStore((state) => state.authRestorationStatus);
  const user = useAuthStore((state) => state.user);
  const runtimeState = useConnectivityStore((state) => state.runtimeState);
  const [isReady, setIsReady] = useState(false);
  const redirectedRef = useRef(false);
  const reportedStatusRef = useRef<string | null>(null);

  // Wait for Firebase auth to be ready before making any decisions
  useEffect(() => {
    let mounted = true;

    waitForAuthReady().then(() => {
      if (mounted) {
        setIsReady(true);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isReady || !authInitialized) return;

    if (authRestorationStatus === 'AUTHENTICATED' && authenticated && user) {
      redirectedRef.current = false;
      reportedStatusRef.current = null;
      connectivityCoordinator.clearStartupFailure('auth-restore');
      return;
    }

    if (authRestorationStatus === 'RECOVERY_REQUIRED') {
      if (reportedStatusRef.current !== authRestorationStatus) {
        reportedStatusRef.current = authRestorationStatus;
        connectivityCoordinator.reportStartupFailure(
          'auth-restore',
          new Error(
            'Your saved sign-in could not be verified. Reconnect and retry to restore access.'
          )
        );
      }
      return;
    }

    if (authRestorationStatus !== 'SIGNED_OUT') return;

    if (runtimeState !== 'ONLINE') {
      if (reportedStatusRef.current !== authRestorationStatus) {
        reportedStatusRef.current = authRestorationStatus;
        connectivityCoordinator.reportStartupFailure(
          'auth-restore',
          new Error('No saved sign-in is available. Connect to PointCraft and sign in to continue.')
        );
      }
      return;
    }

    connectivityCoordinator.clearStartupFailure('auth-restore');
    if (redirectedRef.current) return;
    const loginPath = getLoginPath(type);
    if (location.pathname === loginPath) return;

    redirectedRef.current = true;
    const fullPath = `${location.pathname}${location.search}${location.hash}`;
    const searchParams = new URLSearchParams({ returnTo: fullPath }).toString();
    router.replace(`${loginPath}?${searchParams}`);
  }, [
    authInitialized,
    authenticated,
    authRestorationStatus,
    isReady,
    location.hash,
    location.pathname,
    location.search,
    router,
    runtimeState,
    type,
    user,
  ]);

  // Show splash screen until we know the auth state
  if (
    !isReady ||
    !authInitialized ||
    authRestorationStatus === 'RESTORING' ||
    authRestorationStatus !== 'AUTHENTICATED' ||
    !authenticated ||
    !user
  ) {
    return <SplashScreen />;
  }

  return <>{children}</>;
}
